import { useCallback, useEffect, useState } from 'react';
import { listApiKeys, createApiKey, revokeApiKey } from '../services/api';
import type { ApiKey, CreatedApiKey } from '../../shared/types';

/**
 * API Keys management section for the account page.
 *
 * Renders inside the AccountSettingsPage when the user's effective
 * apiAccess feature is true (controlled at the parent level via the
 * tab visibility). Self-contained: handles list, create, reveal-once
 * modal, and revoke.
 */

function formatRelative(iso: string | null): string {
  if (!iso) return 'jamais';
  const date = new Date(iso);
  const diffMs = Date.now() - date.getTime();
  const diffMin = Math.floor(diffMs / 60_000);
  if (diffMin < 1) return "à l'instant";
  if (diffMin < 60) return `il y a ${diffMin} min`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `il y a ${diffHr} h`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 30) return `il y a ${diffDay} j`;
  return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

export default function ApiKeysSection() {
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Create form
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [createError, setCreateError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Reveal modal (one-shot display of the plain key)
  const [revealedKey, setRevealedKey] = useState<CreatedApiKey | null>(null);
  const [copied, setCopied] = useState(false);

  // Revoke confirm state
  const [revokingId, setRevokingId] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listApiKeys();
      setKeys(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleCreate = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      const trimmed = newName.trim();
      if (!trimmed) return;
      setSubmitting(true);
      setCreateError(null);
      try {
        const created = await createApiKey(trimmed);
        setRevealedKey(created);
        setNewName('');
        setCreating(false);
        await refresh();
      } catch (err) {
        setCreateError(err instanceof Error ? err.message : 'Création échouée');
      } finally {
        setSubmitting(false);
      }
    },
    [newName, refresh],
  );

  const handleRevoke = useCallback(
    async (id: string) => {
      try {
        await revokeApiKey(id);
        await refresh();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Révocation échouée');
      } finally {
        setRevokingId(null);
      }
    },
    [refresh],
  );

  const handleCopy = useCallback(async () => {
    if (!revealedKey) return;
    try {
      await navigator.clipboard.writeText(revealedKey.plainKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore — user can still copy manually
    }
  }, [revealedKey]);

  const activeKeys = keys.filter((k) => !k.revokedAt);
  const revokedKeys = keys.filter((k) => k.revokedAt);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Clés API</h2>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
            Utilisez ces clés pour authentifier les intégrations serveur-à-serveur avec
            l'API Evinly (analyse de conformité Loi Évin).
          </p>
        </div>
        <a
          href="/docs/developer"
          target="_blank"
          rel="noreferrer"
          className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg border border-violet-300 dark:border-violet-700 text-violet-700 dark:text-violet-300 hover:bg-violet-50 dark:hover:bg-violet-900/20 transition-colors"
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
            <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
          </svg>
          Documentation API
        </a>
      </div>

      {/* Banner: critical security note */}
      <div className="rounded-lg border border-amber-200 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-900/10 p-4 text-sm text-amber-800 dark:text-amber-200">
        <div className="font-medium">À conserver en lieu sûr</div>
        <p className="mt-1">
          La valeur complète de chaque clé est <strong>affichée une seule fois</strong> à sa
          création. Stockez-la immédiatement dans un gestionnaire de secrets. Si vous la perdez,
          révoquez-la et créez-en une nouvelle.
        </p>
      </div>

      {/* List + create button */}
      <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-slate-700">
          <h3 className="text-sm font-semibold text-slate-900 dark:text-white">
            Vos clés actives ({activeKeys.length})
          </h3>
          {!creating && (
            <button
              onClick={() => {
                setCreating(true);
                setCreateError(null);
              }}
              className="px-3 py-1.5 text-xs font-medium rounded-lg bg-violet-600 text-white hover:bg-violet-700 transition-colors"
            >
              + Nouvelle clé
            </button>
          )}
        </div>

        {/* Inline create form */}
        {creating && (
          <form
            onSubmit={handleCreate}
            className="px-5 py-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 space-y-3"
          >
            <label className="block text-xs font-medium text-slate-700 dark:text-slate-300">
              Nom de la clé
            </label>
            <input
              type="text"
              autoFocus
              maxLength={64}
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder='Ex: "BBCA production", "CI staging"'
              className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-violet-500"
            />
            {createError && (
              <p className="text-xs text-red-600 dark:text-red-400">{createError}</p>
            )}
            <div className="flex gap-2">
              <button
                type="submit"
                disabled={submitting || !newName.trim()}
                className="px-3 py-1.5 text-xs font-medium rounded-lg bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {submitting ? 'Création...' : 'Créer la clé'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setCreating(false);
                  setNewName('');
                  setCreateError(null);
                }}
                className="px-3 py-1.5 text-xs font-medium rounded-lg text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
              >
                Annuler
              </button>
            </div>
          </form>
        )}

        {/* List */}
        {loading ? (
          <div className="px-5 py-8 text-center text-sm text-slate-500">Chargement...</div>
        ) : error ? (
          <div className="px-5 py-8 text-center text-sm text-red-600">{error}</div>
        ) : activeKeys.length === 0 ? (
          <div className="px-5 py-8 text-center text-sm text-slate-500">
            Aucune clé API active. Créez-en une pour commencer.
          </div>
        ) : (
          <ul className="divide-y divide-slate-200 dark:divide-slate-700">
            {activeKeys.map((key) => (
              <li key={key.id} className="px-5 py-4 flex items-center gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-slate-900 dark:text-white truncate">
                      {key.name}
                    </span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300">
                      {key.environment}
                    </span>
                  </div>
                  <div className="mt-1 flex items-center gap-3 text-xs text-slate-500 dark:text-slate-400">
                    <code className="font-mono">{key.prefix}…</code>
                    <span>•</span>
                    <span>créée le {formatDate(key.createdAt)}</span>
                    <span>•</span>
                    <span>utilisée {formatRelative(key.lastUsedAt)}</span>
                  </div>
                </div>
                {revokingId === key.id ? (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleRevoke(key.id)}
                      className="px-3 py-1.5 text-xs font-medium rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors"
                    >
                      Confirmer
                    </button>
                    <button
                      onClick={() => setRevokingId(null)}
                      className="px-3 py-1.5 text-xs font-medium rounded-lg text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                    >
                      Annuler
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setRevokingId(key.id)}
                    className="px-3 py-1.5 text-xs font-medium rounded-lg text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                  >
                    Révoquer
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Revoked keys (collapsed by default — shown for audit) */}
      {revokedKeys.length > 0 && (
        <details className="rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/30">
          <summary className="px-5 py-3 text-sm font-medium text-slate-700 dark:text-slate-300 cursor-pointer">
            Clés révoquées ({revokedKeys.length})
          </summary>
          <ul className="divide-y divide-slate-200 dark:divide-slate-700">
            {revokedKeys.map((key) => (
              <li key={key.id} className="px-5 py-3 text-xs text-slate-500 dark:text-slate-400 flex items-center gap-3">
                <code className="font-mono">{key.prefix}…</code>
                <span className="truncate flex-1">{key.name}</span>
                <span>révoquée {formatRelative(key.revokedAt)}</span>
              </li>
            ))}
          </ul>
        </details>
      )}

      {/* Reveal modal — shown ONCE after creation */}
      {revealedKey && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="max-w-lg w-full rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 p-6 space-y-5 shadow-2xl">
            <div>
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                Votre clé API « {revealedKey.name} »
              </h3>
              <p className="mt-1 text-sm text-amber-700 dark:text-amber-300">
                ⚠ Cette valeur ne sera <strong>plus jamais affichée</strong>. Copiez-la maintenant.
              </p>
            </div>

            <div className="rounded-lg bg-slate-100 dark:bg-slate-800 p-4 font-mono text-sm break-all text-slate-900 dark:text-slate-100 select-all">
              {revealedKey.plainKey}
            </div>

            <div className="flex gap-2">
              <button
                onClick={handleCopy}
                className="flex-1 px-4 py-2 text-sm font-medium rounded-lg bg-violet-600 text-white hover:bg-violet-700 transition-colors"
              >
                {copied ? '✓ Copiée' : 'Copier la clé'}
              </button>
              <button
                onClick={() => {
                  setRevealedKey(null);
                  setCopied(false);
                }}
                className="px-4 py-2 text-sm font-medium rounded-lg text-slate-700 dark:text-slate-200 border border-slate-300 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              >
                J'ai copié
              </button>
            </div>

            <p className="text-xs text-slate-500 dark:text-slate-400">
              Stockez cette clé dans un gestionnaire de secrets (Vault, AWS Secrets Manager,
              Google Secret Manager…) et ne la commitez jamais dans un dépôt git.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
